import {subDays} from 'date-fns';

export type DateRange = {
  readonly from: string;
  readonly to: string;
};

const LOCAL_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

export const MAX_MEASUREMENT_HISTORY_WINDOW_DAYS = 90;

const pad2 = (n: number): string => String(n).padStart(2, '0');

/** Browser-local calendar date as `YYYY-MM-DD`. */
export const toLocalDateString = (date: Date): string =>
  `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;

/** Today in the browser's timezone as `YYYY-MM-DD`. */
export const todayLocalDateString = (): string => toLocalDateString(new Date());

export const isLocalDateString = (value: string): boolean => {
  if (!LOCAL_DATE_PATTERN.test(value)) {
    return false;
  }
  return !Number.isNaN(Date.parse(`${value}T00:00:00.000Z`));
};

/** Parse `YYYY-MM-DD` into a local Date at noon (stable for the date picker). */
export const parseLocalDateString = (value: string): Date => {
  const [y, m, d] = value.split('-').map(Number);
  return new Date(y ?? 0, (m ?? 1) - 1, d ?? 1, 12, 0, 0, 0);
};

/** Inclusive calendar-day count; same rule as the server (UTC midnight of the strings). */
export const inclusiveDayCount = (from: string, to: string): number => {
  const fromMs = Date.parse(`${from}T00:00:00.000Z`);
  const toMs = Date.parse(`${to}T00:00:00.000Z`);
  return Math.floor((toMs - fromMs) / MS_PER_DAY) + 1;
};

/** The last 90 inclusive days ending today; exactly the server cap. */
export const defaultMeasurementDateRange = (now: Date = new Date()): DateRange => ({
  from: toLocalDateString(subDays(now, MAX_MEASUREMENT_HISTORY_WINDOW_DAYS - 1)),
  to: toLocalDateString(now),
});

export const isValidMeasurementDateRange = (from: string, to: string): boolean => {
  if (!isLocalDateString(from) || !isLocalDateString(to)) {
    return false;
  }
  if (from > to) {
    return false;
  }
  return inclusiveDayCount(from, to) <= MAX_MEASUREMENT_HISTORY_WINDOW_DAYS;
};
